import { useCallback, useRef, useMemo, useEffect } from 'react';
import { useFieldActions } from '../../../contexts/field-actions-context';
import { FIELD_TYPES } from '../../../constants';
import { useTranslation } from '../../../contexts/translation-context';
import { useServiceOptionsBase } from './use-service-options-base';
export const OTHER_OPTION_ID = '00000000-0000-0000-0000-000000000000';
export const useServiceDropdownOptions = props => {
  const {
    actions
  } = useFieldActions();
  const {
    t
  } = useTranslation();
  const {
    addOther,
    addOtherLabel
  } = props;
  const otherLabel = addOtherLabel || t('add-other.default-other-option-label');
  const shouldFetchData = useRef(true);
  const serviceIds = useMemo(() => {
    var _props$options;
    return ((_props$options = props.options) == null ? void 0 : _props$options.map(option => option.id ?? '')) ?? [];
  }, [props.options]);

  // Post-processing: add "other" option if needed
  const postProcessOptions = useCallback(options => {
    if (addOther) {
      return [...options, {
        value: otherLabel,
        label: otherLabel,
        id: OTHER_OPTION_ID
      }];
    }
    return options;
  }, [addOther, otherLabel]);
  const {
    isLoading,
    options,
    isEmpty,
    shouldRender,
    setIsLoading,
    setOptions,
    setIsEmpty,
    processOptions
  } = useServiceOptionsBase({
    fieldType: FIELD_TYPES.SERVICES_DROPDOWN,
    isEditor: props.isEditor,
    postProcessOptions
  });

  // Custom fetch logic for specific service IDs
  const customFetchLogic = useCallback(async () => {
    if (shouldFetchData.current && actions != null && actions.fetchServices && serviceIds.length > 0) {
      shouldFetchData.current = false;
      setIsLoading(true);
      const relevantServices = await actions.fetchServices({
        serviceIds
      });
      const processedOptions = processOptions(relevantServices);
      setIsEmpty(!addOther && processedOptions.length === 0);
      setOptions(processedOptions);
      setIsLoading(false);
    }
  }, [actions, serviceIds, processOptions, addOther, setIsLoading, setOptions, setIsEmpty]);

  // Handle empty state calculation for initial data
  useEffect(() => {
    if (!props.isEditor && options.length > 0) {
      const optionCountWithoutOther = addOther ? options.length - 1 : options.length;
      setIsEmpty(optionCountWithoutOther === 0);
    }
  }, [options, addOther, setIsEmpty, props.isEditor]);
  const onFocus = useCallback(() => {
    if (props.onFocus) {
      props.onFocus();
    }
    if (shouldFetchData.current) {
      customFetchLogic();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFetchLogic, props.onFocus]);
  const onChange = useCallback(value => {
    if (props.onChange) {
      props.onChange(value);
    }
    shouldFetchData.current = true;
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [props.onChange]);
  return {
    isLoading,
    options,
    isEmpty,
    onFocus,
    onChange,
    shouldRender
  };
};
//# sourceMappingURL=use-service-dropdown-options.js.map